import { useEffect, useState } from 'react';
import api from '../api/axios';

interface CatalogOrder {
  id: number;
  group_id: string | null;
  customer_name: string;
  customer_phone: string;
  inventory_id: number;
  product_name: string;
  sku: string;
  selling_price: number;
  stock: number;
  quantity: number;
  note: string | null;
  status: 'menunggu' | 'diproses' | 'selesai' | 'dibatalkan';
  sale_id: number | null;
  created_at: string;
}

type FilterStatus = 'all' | CatalogOrder['status'];

const rupiahFormat = (n: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

const statusConfig = {
  menunggu: { label: 'Menunggu', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  diproses: { label: 'Diproses', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  selesai: { label: 'Selesai', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  dibatalkan: { label: 'Dibatalkan', color: 'bg-red-100 text-red-600 border-red-200' },
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const formatPhone = (phone: string) => {
  let p = phone.replace(/\D/g, '');
  if (p.startsWith('0')) p = '62' + p.slice(1);
  if (!p.startsWith('62')) p = '62' + p;
  return p;
};

export default function PesananKatalog() {
  const [orders, setOrders] = useState<CatalogOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [actionId, setActionId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get('/catalog-orders/getAll');
      setOrders(res.data);
    } catch {
      setError('Gagal memuat pesanan katalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const handleStatus = async (order: CatalogOrder, status: 'diproses' | 'dibatalkan') => {
    setActionId(order.id);
    setError('');
    try {
      await api.patch(`/catalog-orders/updateStatus/${order.id}`, { status });
      fetchOrders();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal mengupdate pesanan');
    } finally {
      setActionId(null);
    }
  };

  const handleComplete = async (order: CatalogOrder) => {
    setActionId(order.id);
    setError('');
    try {
      await api.post(`/catalog-orders/complete/${order.id}`);
      fetchOrders();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal menyelesaikan pesanan');
    } finally {
      setActionId(null);
    }
  };

  const filtered = orders.filter((order) => filterStatus === 'all' || order.status === filterStatus);
  const counts = {
    all: orders.length,
    menunggu: orders.filter((o) => o.status === 'menunggu').length,
    diproses: orders.filter((o) => o.status === 'diproses').length,
    selesai: orders.filter((o) => o.status === 'selesai').length,
    dibatalkan: orders.filter((o) => o.status === 'dibatalkan').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Pesanan Katalog</h1>
          <p className="text-sm text-gray-500 mt-0.5">Kelola pesanan ready stock dari halaman katalog</p>
        </div>
        <button onClick={fetchOrders}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition">
          Refresh
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}

      <div className="flex gap-2 flex-wrap">
        {([
          ['all', 'Semua', counts.all],
          ['menunggu', 'Menunggu', counts.menunggu],
          ['diproses', 'Diproses', counts.diproses],
          ['selesai', 'Selesai', counts.selesai],
          ['dibatalkan', 'Dibatalkan', counts.dibatalkan],
        ] as const).map(([val, label, count]) => (
          <button key={val} onClick={() => setFilterStatus(val)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition ${
              filterStatus === val ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}>
            {label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${filterStatus === val ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">Memuat pesanan...</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <p className="font-medium text-gray-500">Tidak ada pesanan katalog</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((order) => {
            const cfg = statusConfig[order.status];
            const total = order.selling_price * order.quantity;
            const busy = actionId === order.id;

            return (
              <div key={order.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        {order.group_id && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-indigo-50 text-indigo-600 border border-indigo-100">
                            {order.group_id}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">#{order.id} · {formatDate(order.created_at)}</span>
                      </div>
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">{order.product_name}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">SKU: {order.sku}</p>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-gray-400">Total</p>
                      <p className="font-bold text-gray-800">{rupiahFormat(total)}</p>
                      <p className="text-xs text-gray-400">{order.quantity} pcs x {rupiahFormat(order.selling_price)}</p>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-50 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="text-sm text-gray-600">
                      <span className="text-gray-400">Customer: </span>
                      <span className="font-medium">{order.customer_name}</span>
                    </div>
                    <a href={`https://wa.me/${formatPhone(order.customer_phone)}`} target="_blank" rel="noreferrer"
                      className="text-sm text-emerald-600 hover:underline font-medium">
                      {order.customer_phone}
                    </a>
                    <div className={`text-sm font-medium ${order.stock < order.quantity ? 'text-red-600' : 'text-gray-600'}`}>
                      Stok saat ini: {order.stock}
                    </div>
                  </div>

                  {order.note && (
                    <div className="mt-2 px-3 py-2 bg-gray-50 rounded-lg text-xs text-gray-500 italic">
                      {order.note}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {order.status === 'menunggu' && (
                      <button disabled={busy} onClick={() => handleStatus(order, 'diproses')}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-medium disabled:opacity-60 transition">
                        Proses
                      </button>
                    )}
                    {order.status === 'diproses' && (
                      <button disabled={busy} onClick={() => handleComplete(order)}
                        className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-medium disabled:opacity-60 transition">
                        Selesaikan Jadi Penjualan
                      </button>
                    )}
                    {['menunggu', 'diproses'].includes(order.status) && (
                      <button disabled={busy} onClick={() => handleStatus(order, 'dibatalkan')}
                        className="px-3 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-xl text-xs font-medium disabled:opacity-60 transition">
                        Batalkan
                      </button>
                    )}
                    <a href={`https://wa.me/${formatPhone(order.customer_phone)}`} target="_blank" rel="noreferrer"
                      className="px-3 py-2 border border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-xl text-xs font-medium transition ml-auto">
                      Chat WA
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
